import { useMemo } from "react"

import type { ColumnDef } from "../types/columns"
import { RUNS_VIEW_MODE, type RunsViewMode } from "../types/viewMode"
import {
  buildMetricColumns,
  topMetricIdsByFrequency,
} from "../utils/metricColumnUtils"
import { useVisibleMetricColumns } from "./useVisibleMetricColumns"

const BASE_RUN_COLUMNS: ColumnDef[] = [
  { key: "status", label: "Status" },
  { key: "runName", label: "Run Name" },
  { key: "agentName", label: "Agent" },
  { key: "testSetName", label: "Test Set" },
  { key: "personaId", label: "Persona" },
  { key: "modelType", label: "Simulator" },
  { key: "numSimulations", label: "Count" },
]

const RUN_ID_COLUMN: ColumnDef = { key: "id", label: "ID" }

// Tags sits last so it lands immediately before the ID column(s) that close
// every table — the run view appends one, the simulation view appends three.
const TAIL_COLUMNS: ColumnDef[] = [
  { key: "created", label: "Created" },
  { key: "createdBy", label: "Created by" },
  { key: "tags", label: "Tags" },
]

const BASE_SIMULATION_COLUMNS: ColumnDef[] = [
  { key: "status", label: "Status" },
  { key: "agentName", label: "Agent" },
  { key: "testSetName", label: "Test Set" },
  { key: "personaId", label: "Persona" },
  { key: "modelType", label: "Simulator" },
]

const CALL_SID_COLUMN: ColumnDef = { key: "callSid", label: "Call SID" }
const SIMULATION_ID_COLUMN: ColumnDef = {
  key: "simulationId",
  label: "ID",
  csvHeader: "Simulation ID",
}
const SIMULATION_RUN_ID_COLUMN: ColumnDef = { key: "runId", label: "Run ID" }

const SIMULATION_ID_COLUMNS: ColumnDef[] = [
  CALL_SID_COLUMN,
  SIMULATION_ID_COLUMN,
  SIMULATION_RUN_ID_COLUMN,
]

const DEFAULT_VISIBLE_SIMULATION_COLUMNS: ColumnDef[] = [
  ...BASE_SIMULATION_COLUMNS,
  SIMULATION_ID_COLUMN,
]

const SIMULATION_TOGGLE_COLUMNS: ColumnDef[] = [
  ...BASE_SIMULATION_COLUMNS,
  ...SIMULATION_ID_COLUMNS,
]

const DEFAULT_VISIBLE_METRICS = 5

interface UseRunsTableColumnsParams {
  viewMode: RunsViewMode
  queryContext: unknown
  runs: Array<{ metric_ids?: string[] }>
  simulations: Array<{ metric_ids?: string[] }>
  metricDisplayNames: Record<string, string>
  metricsMetadataReady: boolean
}

export function useRunsTableColumns({
  viewMode,
  queryContext,
  runs,
  simulations,
  metricDisplayNames,
  metricsMetadataReady,
}: UseRunsTableColumnsParams) {
  const orderedMetricIds = useMemo(() => topMetricIdsByFrequency(runs), [runs])
  const orderedSimulationMetricIds = useMemo(
    () => topMetricIdsByFrequency(simulations),
    [simulations],
  )

  const allColumns = useMemo(
    () => [
      ...BASE_RUN_COLUMNS,
      ...(metricsMetadataReady
        ? buildMetricColumns(orderedMetricIds, metricDisplayNames)
        : []),
      ...TAIL_COLUMNS,
      RUN_ID_COLUMN,
    ],
    [metricDisplayNames, metricsMetadataReady, orderedMetricIds],
  )

  const simulationColumns = useMemo(
    () => [
      ...BASE_SIMULATION_COLUMNS,
      ...(metricsMetadataReady
        ? buildMetricColumns(orderedSimulationMetricIds, metricDisplayNames)
        : []),
      ...TAIL_COLUMNS,
      ...SIMULATION_ID_COLUMNS,
    ],
    [metricDisplayNames, metricsMetadataReady, orderedSimulationMetricIds],
  )

  const runVisibilityColumns = useMemo(
    () => [...BASE_RUN_COLUMNS, RUN_ID_COLUMN],
    [],
  )
  const { visibleColumns, setVisibleColumns } = useVisibleMetricColumns({
    viewMode,
    queryContext,
    orderedMetricIds,
    orderedSimMetricIds: orderedSimulationMetricIds,
    metricDisplayNames,
    metricsMetadataReady,
    baseRunColumns: runVisibilityColumns,
    baseSimColumns: DEFAULT_VISIBLE_SIMULATION_COLUMNS,
    tailColumns: TAIL_COLUMNS,
    defaultVisibleMetrics: DEFAULT_VISIBLE_METRICS,
  })

  return {
    activeColumns:
      viewMode === RUNS_VIEW_MODE.RUNS ? allColumns : simulationColumns,
    allColumns,
    baseRunColumns: BASE_RUN_COLUMNS,
    baseSimulationColumns: SIMULATION_TOGGLE_COLUMNS,
    setVisibleColumns,
    simulationColumns,
    visibleColumns,
  }
}
